import { HttpBackend, HttpClient, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { BASE_PATH } from '../api';
import { AuthTokenService } from './auth-token.service';
import { readUsersMsRefreshToken, storeUsersMsRefreshToken, type UsersMsTokens } from './users-ms-session';

/** The API's answer to an access token users ms no longer accepts (UserTokenValidationMiddleware). */
const TOKEN_INVALID_STATUS = 'TokenInvalid';

/** Login endpoints answer for themselves: their 401 / 403 are never a stale session. */
const LOGIN_PATH = /\/api\/login(\/|$|\?)/;

/**
 * Renews the users ms access token: POST /api/login/refreshToken { refreshToken } (proxied by the client API).
 * Sent through HttpBackend so it skips every interceptor (no loop, no global error toast). Concurrent
 * callers share one in-flight refresh.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly basePath = inject(BASE_PATH, { optional: true }) ?? '';
  private readonly authToken = inject(AuthTokenService);
  private inFlight: Observable<string> | null = null;

  /** Emits the new access token, or errors when there is no refresh token or users ms refuses it. */
  refresh(): Observable<string> {
    if (this.inFlight) {
      return this.inFlight;
    }

    const refreshToken = readUsersMsRefreshToken();
    if (!refreshToken) {
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'No refresh token' }));
    }

    this.inFlight = this.http
      .post<UsersMsTokens>(`${this.basePath}/api/login/refreshToken`, { refreshToken })
      .pipe(
        map(tokens => {
          if (!tokens?.accessToken) {
            throw new HttpErrorResponse({ status: 401, statusText: 'Refresh returned no access token' });
          }
          this.authToken.setToken(tokens.accessToken);
          // Cognito usually keeps the same refresh token; store whatever came back with the new expiry.
          storeUsersMsRefreshToken(tokens);
          return tokens.accessToken;
        }),
        finalize(() => (this.inFlight = null)),
        shareReplay(1),
      );
    return this.inFlight;
  }
}

function isTokenInvalid(error: unknown): boolean {
  return error instanceof HttpErrorResponse
    && error.status === 403
    && (error.error as { status?: string } | null)?.status === TOKEN_INVALID_STATUS;
}

function withToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
}

/**
 * On 403 TokenInvalid (the access token expired or was revoked): refresh it and repeat the request once with the
 * new token. When the refresh fails the request fails with 401, which `httpErrorInterceptor` turns into a logout.
 * Registered last, so it sees the raw response before `httpErrorInterceptor` handles 403.
 */
export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  if (LOGIN_PATH.test(req.url) || !req.headers.has('Authorization')) {
    return next(req);
  }

  const refresher = inject(TokenRefreshService);
  return next(req).pipe(
    catchError(error => {
      if (!isTokenInvalid(error)) {
        return throwError(() => error);
      }
      return refresher.refresh().pipe(
        catchError(() => throwError(() => new HttpErrorResponse({
          status: 401,
          statusText: 'Session expired',
          url: req.url,
        }))),
        switchMap(token => next(withToken(req, token))),
      );
    }),
  );
};
